import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useEffect } from 'react'
import { cn } from '@/lib/utils'
import { ProtectedRoute } from '../components/ProtectedRoute'
import { MustChangePasswordForm } from '../components/auth/MustChangePasswordForm'
import { BillingSetupBanner } from '../components/BillingSetupBanner'
import { StoreSuspendedBanner } from '../components/StoreSuspendedBanner'
import { BillingGateModal } from '../pages/billing/BillingPage'
import { Navbar } from '../components/layout/Navbar'
import { Sidebar } from '../components/layout/Sidebar'
import { useAuth } from '../context/AuthContext'
import { useCurrentStore } from '../context/CurrentStoreContext'
import { OrganisationDisplayProvider } from '../context/OrganisationDisplayContext'
import { CurrentStoreProvider } from '../context/CurrentStoreContext'
import { useUI } from '../context/UIContext'
import { SupportAccessBanner } from '../components/SupportAccessBanner'
import { firstStaffModulePath, staffCanAccessPath } from '../config/staffModules'
import { formatDocumentTitle, matchDashboardPageTitle } from '../config/pageTitles'
import { resolveBillingAccessMode } from '../lib/billingAccess'

function DashboardShell() {
  const { pathname } = useLocation()
  const { user } = useAuth()
  const { currentStore, storeRecord } = useCurrentStore()
  const { posFocusMode, setPosFocusMode } = useUI()
  const passwordGate = Boolean(user?.mustChangePassword)
  const billingRequired = Boolean(currentStore?.billingRequired ?? user?.billingRequired)
  const subscriptionStatus = currentStore?.subscription?.status ?? null
  const billingMode = resolveBillingAccessMode({
    billingRequired,
    subscriptionStatus,
  })
  const onBillingRoute = pathname === '/billing' || pathname.startsWith('/billing/')
  const billingHardGate = billingMode === 'hard' && !passwordGate && !onBillingRoute
  const billingSoftBanner = billingMode === 'soft' && !passwordGate && !onBillingRoute
  const supportSession = Boolean(user?.supportSession)
  const { title, subtitle } = matchDashboardPageTitle(pathname)
  const mainFullWidth =
    pathname === '/pos/sales' || pathname === '/settings' || pathname.startsWith('/billing')
  const isPosSales = pathname === '/pos/sales'
  /** Hide admin sidebar so catalog + cart use full width. */
  const posFocusActive = isPosSales && posFocusMode
  const contentLocked = passwordGate || billingHardGate

  useEffect(() => {
    document.title = formatDocumentTitle(title)
  }, [title])

  useEffect(() => {
    if (!posFocusActive) return
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setPosFocusMode(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [posFocusActive, setPosFocusMode])

  if (
    user?.role === 'staff' &&
    !passwordGate &&
    !billingHardGate &&
    !staffCanAccessPath(pathname, user.role, user.modules)
  ) {
    const target = firstStaffModulePath(user.modules)
    if (target !== pathname) {
      return <Navigate to={target} replace />
    }
  }

  return (
    <>
      <SupportAccessBanner />
      <div
        className={cn(
          'relative flex h-dvh max-h-dvh overflow-hidden bg-background text-foreground',
          supportSession ? 'pt-12' : '',
        )}
      >
        <div className="pointer-events-none fixed inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-primary/20 blur-3xl float-animation" />
          <div className="absolute top-1/2 -left-40 h-80 w-80 rounded-full bg-accent/15 blur-3xl float-animation-delayed" />
          <div className="absolute -bottom-40 right-1/3 h-72 w-72 rounded-full bg-primary/10 blur-3xl float-animation-slow" />
        </div>
        {posFocusActive ? null : <Sidebar />}
        <div className="relative z-10 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <Navbar title={title} subtitle={subtitle} posFocusActive={posFocusActive} />
          {storeRecord?.suspendedAt ? <StoreSuspendedBanner reason={storeRecord.suspensionReason} /> : null}
          {billingSoftBanner ? (
            <BillingSetupBanner
              subscriptionStatus={subscriptionStatus}
              complimentary={Boolean(currentStore?.subscription?.complimentary)}
            />
          ) : null}
          <main
            className={cn(
              'relative min-h-0 flex-1',
              isPosSales
                ? cn(
                    'overflow-hidden px-3 py-3 sm:px-4 sm:py-4',
                    posFocusActive ? 'lg:px-4 lg:py-3' : 'lg:px-6 lg:py-4',
                  )
                : 'overflow-y-auto px-4 py-6 sm:px-6 lg:px-8 lg:py-10',
            )}
          >
            <div
              className={cn(
                'relative mx-auto w-full',
                isPosSales ? 'flex h-full min-h-0 flex-col' : '',
                mainFullWidth ? 'max-w-none' : 'max-w-7xl',
                contentLocked ? 'pointer-events-none opacity-[0.32]' : '',
              )}
              aria-hidden={contentLocked}
            >
              <Outlet />
            </div>
            {passwordGate ? (
              <div
                className="absolute inset-0 z-30 flex justify-center overflow-y-auto bg-background/70 px-4 py-10 backdrop-blur-sm sm:items-center sm:py-6"
                role="presentation"
              >
                <div className="my-auto h-fit w-full max-w-md rounded-2xl border border-border bg-card p-6 text-card-foreground shadow-[var(--shadow-modal)] sm:p-8">
                  <MustChangePasswordForm />
                </div>
              </div>
            ) : billingHardGate ? (
              <BillingGateModal />
            ) : null}
          </main>
        </div>
      </div>
    </>
  )
}

export function DashboardLayout() {
  return (
    <ProtectedRoute>
      <OrganisationDisplayProvider>
        <CurrentStoreProvider>
          <DashboardShell />
        </CurrentStoreProvider>
      </OrganisationDisplayProvider>
    </ProtectedRoute>
  )
}
